import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import BrandShell, { BrandBackLink } from "../components/BrandShell";
import { useAuth } from "../lib/auth";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { BrandVerifyBanner } from "../components/BrandVerifyBanner";
import { CampaignQuestionsEditor } from "../components/CampaignQuestionsEditor";
import { DeliverableSpecEditor, DeliverableSpecTable, DEFAULT_SPEC } from "../components/CampaignSpec";
import { uploadFile } from "../lib/upload";
import { useDraft } from "../lib/useDraft";
import { DraftBar } from "../components/DraftBar";
import {
  Megaphone, Film, Link as LinkIcon, Paperclip, Truck, Target, Wallet as WalletIcon, Handshake,
  Plus, X, ChevronRight, ChevronLeft, Check, Sparkles, ClipboardCheck, Wand2, Image as ImageIcon, Video as VideoIcon, Trash2,
} from "lucide-react";

const NICHES = ["Fashion", "Beauty", "Fitness", "Food", "Travel", "Tech", "Gaming", "Lifestyle", "Startups", "Product Reviews", "AI"];
const PLATFORMS = [
  { key: "instagram",     label: "Instagram" },
  { key: "tiktok",        label: "TikTok" },
  { key: "youtube",       label: "YouTube" },
  { key: "facebook",      label: "Facebook" },
  { key: "snapchat",      label: "Snapchat" },
  { key: "twitter",       label: "X (Twitter)" },
  { key: "ad_advertising", label: "Ad advertising" },
];

const STEPS = [
  { key: "basics", label: "Basics", icon: Megaphone, hint: "Title, description, deliverables" },
  { key: "pricing", label: "Pricing", icon: WalletIcon, hint: "Budget & timeline" },
  { key: "requirements", label: "Requirements", icon: Film, hint: "Niches, platforms, brief" },
  { key: "review", label: "Review", icon: ClipboardCheck, hint: "Publish" },
];

export default function CampaignNew() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiIdea, setAiIdea] = useState("");
  const [showAi, setShowAi] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    budget: 500,
    niches: [],
    platforms: [],
    deadline_days: 14,
    deliverables: "",
    pricing_mode: "fixed",
    raw_footage_required: false,
    edited_content_required: true,
    inspiration_links: [""],
    brief_files: [],
    targets: { ads_per_week: 0, views: 0, engagement: 0, conversions: 0, sales: 0 },
    needs_shipping: false,
    shipping_note: "",
    questions: [],
    spec: { ...DEFAULT_SPEC },
    cover_url: "",
    cover_video_url: "",
  });
  const [coverUploading, setCoverUploading] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/auth?mode=login"); return; }
    if (user.role !== "brand") { navigate("/creator"); return; }
  }, [user, loading, navigate]);

  const toggle = (arr, x) => (arr.includes(x) ? arr.filter((v) => v !== x) : [...arr, x]);
  const initialFormRef = useRef(null);
  if (!initialFormRef.current) initialFormRef.current = form;
  const draft = useDraft("campaign-new", {
    value: { form, step },
    sync: true,
    isEmpty: (v) => { const f = v?.form || {}; return !f.title?.trim() && !f.description?.trim() && !(f.brief_files || []).length && !f.cover_url && !f.cover_video_url; },
    onRestore: (v) => { if (v?.form) setForm({ ...initialFormRef.current, ...v.form }); if (typeof v?.step === "number") setStep(v.step); },
  });
  const startFresh = async () => { await draft.clear(); setForm(initialFormRef.current); setStep(0); toast("Started a fresh brief"); };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTarget = (k, v) => set("targets", { ...form.targets, [k]: Number(v) || 0 });
  const setLink = (i, v) => set("inspiration_links", form.inspiration_links.map((l, idx) => (idx === i ? v : l)));
  const addLink = () => set("inspiration_links", [...form.inspiration_links, ""]);
  const removeLink = (i) => set("inspiration_links", form.inspiration_links.filter((_, idx) => idx !== i));

  const uploadBrief = async (e) => {    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await uploadFile(file);
      set("brief_files", [...form.brief_files, { name: file.name, url: data.url }]);
    } catch { /* toast shown by uploadFile */ }
    e.target.value = "";
  };
  const removeBriefFile = (i) => set("brief_files", form.brief_files.filter((_, idx) => idx !== i));

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    if (!isImage && !isVideo) {
      toast.error("Please choose an image or a short video");
      e.target.value = "";
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Cover must be under 25 MB — trim the video or compress the image");
      e.target.value = "";
      return;
    }
    setCoverUploading(true);
    try {
      const data = await uploadFile(file, { label: isVideo ? "cover video" : "cover image" });
      if (isVideo) {
        set("cover_video_url", data.url);
        if (!form.cover_url) toast.success("Cover video added");
        else toast.success("Cover video added — it will autoplay on the detail page");
      } else {
        set("cover_url", data.url);
        toast.success("Cover image added");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setCoverUploading(false);
      e.target.value = "";
    }
  };

  const runAiAssist = async () => {
    if (!aiIdea.trim() || aiIdea.trim().length < 6) { toast.error("Give the AI a bit more to work with (a sentence or two)"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/brand/brief-assist", {
        idea: aiIdea.trim(),
        niches: form.niches,
        platforms: form.platforms,
        budget: Number(form.budget) || null,
      });
      set("description", data.description);
      set("deliverables", data.deliverables);
      toast.success("Brief drafted — edit anything you'd like");
      setShowAi(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "AI is busy — try again in a sec");
    } finally { setAiBusy(false); }
  };

  const validateStep = () => {
    if (step === 0) {
      if (!form.title.trim()) return "Add a campaign title";
      if (!form.description.trim()) return "Add a description so creators know what you're after";
    }
    if (step === 1) {
      if (Number(form.budget) <= 0) return "Set a budget above £0";
      if (Number(form.deadline_days) < 1) return "Application window must be at least 1 day";
    }
    if (step === 2) {
      if (form.platforms.length === 0) return "Pick at least one platform";
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/campaigns", {
        ...form,
        budget: Number(form.budget),
        deadline_days: Number(form.deadline_days),
        inspiration_links: form.inspiration_links.filter((l) => l.trim()),
      });
      toast.success(data.brand_verified === false ? "Campaign saved — it goes live to creators once your verification is approved" : "Campaign posted");
      await draft.clear();
      navigate(`/campaigns/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <BrandShell title="Create campaign">
      <SEO title="New campaign · Nexus" description="Post a creator marketing campaign." path="/campaigns/new" />
      <div className="p-6 lg:p-10 max-w-4xl">
        <BrandBackLink to="/brand" label="Back to dashboard" />
        <div className="mb-6 space-y-3">
          <BrandVerifyBanner />
          <DraftBar draft={draft} onStartFresh={startFresh} />
        </div>
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3 flex items-center gap-2">
            <Megaphone className="h-4 w-4" /> New campaign
          </div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter">
            Get pitched by the <span className="nexus-gradient-text">right creators</span>.
          </h1>
          <p className="text-zinc-400 mt-2 text-sm">
            Four steps. About 90 seconds. Every applicant gets an AI match score.
          </p>
        </div>

        {/* Stepper */}
        <div className="grid grid-cols-4 gap-2 mb-8" data-testid="wizard-stepper">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i < step;
            const active = i === step;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => i < step && setStep(i)}
                className={`text-left rounded-2xl p-3 border transition-colors ${
                  active ? "border-nexus-purple bg-nexus-purple/10 nexus-border-glow"
                  : done ? "border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10"
                  : "border-white/10 bg-white/[0.02]"
                }`}
                data-testid={`wizard-step-${s.key}`}
              >
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-400">
                  <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold ${
                    done ? "bg-emerald-500/30 text-emerald-200"
                    : active ? "bg-nexus-purple text-white"
                    : "bg-white/10 text-zinc-400"
                  }`}>
                    {done ? <Check className="h-3 w-3" /> : i + 1}
                  </span>
                  Step {i + 1}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <Icon className={`h-4 w-4 ${active ? "text-nexus-pink" : done ? "text-emerald-300" : "text-zinc-500"}`} />
                  <div className={`font-semibold text-sm ${active ? "text-white" : "text-zinc-300"}`}>{s.label}</div>
                </div>
                <div className="text-[11px] text-zinc-500 mt-1 hidden md:block">{s.hint}</div>
              </button>
            );
          })}
        </div>

        <div className="glass rounded-2xl p-6 md:p-8 space-y-6" data-testid="campaign-new-form">
          {step === 0 && (
            <div className="space-y-5" data-testid="step-basics">
              {/* Campaign cover — image or short video shown on cards + detail */}
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
                  <ImageIcon className="h-3.5 w-3.5" /> Cover
                  <span className="text-[10px] normal-case tracking-normal text-zinc-500">· image or short video</span>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
                  {form.cover_video_url ? (
                    <video src={form.cover_video_url} className="w-full aspect-[16/9] object-cover bg-black" autoPlay muted loop playsInline data-testid="campaign-cover-video-preview" />
                  ) : form.cover_url ? (
                    <img src={form.cover_url} alt="Campaign cover" className="w-full aspect-[16/9] object-cover bg-black" data-testid="campaign-cover-image-preview" />
                  ) : (
                    <div className="w-full aspect-[16/9] flex items-center justify-center text-zinc-500 text-xs">
                      <div className="text-center">
                        <ImageIcon className="h-8 w-8 mx-auto mb-2 text-zinc-600" />
                        <div>Upload a hero image or a 5–10s teaser video</div>
                        <div className="text-[10px] text-zinc-600 mt-1">Shown on the campaigns grid and the detail page</div>
                      </div>
                    </div>
                  )}
                  <div className="p-3 flex flex-wrap items-center gap-2 border-t border-white/5 bg-black/40">
                    <label className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/15 text-xs text-zinc-200 hover:bg-white/10 cursor-pointer transition-colors ${coverUploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="campaign-cover-upload">
                      {coverUploading ? "Uploading…" : (<><ImageIcon className="h-3.5 w-3.5" /> {form.cover_url || form.cover_video_url ? "Replace cover" : "Upload cover"}</>)}
                      <input type="file" onChange={uploadCover} className="hidden" accept="image/*,video/mp4,video/webm,video/quicktime" />
                    </label>
                    {(form.cover_url || form.cover_video_url) && (
                      <>
                        {form.cover_video_url && (
                          <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-nexus-pink/15 text-nexus-pink border border-nexus-pink/30 inline-flex items-center gap-1">
                            <VideoIcon className="h-3 w-3" /> Video
                          </span>
                        )}
                        {form.cover_url && !form.cover_video_url && (
                          <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-white/5 text-zinc-300 border border-white/10 inline-flex items-center gap-1">
                            <ImageIcon className="h-3 w-3" /> Image
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => { set("cover_url", ""); set("cover_video_url", ""); }}
                          className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-400 hover:text-nexus-pink"
                          data-testid="campaign-cover-remove"
                        >
                          <Trash2 className="h-3 w-3" /> Remove
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* AI Brief Assist */}
              <div className="rounded-xl border border-nexus-purple/30 bg-nexus-purple/[0.06] p-4">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Wand2 className="h-4 w-4 text-nexus-pink" />
                    <div className="text-xs uppercase tracking-widest text-zinc-300">AI Brief Assist</div>
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-nexus-pink/15 text-nexus-pink">Beta</span>
                  </div>
                  <button type="button" onClick={() => setShowAi((v) => !v)} className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white" data-testid="ai-assist-toggle">
                    {showAi ? "Hide" : "Try it"}
                  </button>
                </div>
                <p className="text-xs text-zinc-400 mt-1.5">Paste a rough idea — Claude drafts a full brief with deliverables in seconds.</p>
                {showAi && (
                  <div className="mt-3 space-y-2" data-testid="ai-assist-panel">
                    <Textarea
                      value={aiIdea}
                      onChange={(e) => setAiIdea(e.target.value)}
                      rows={3}
                      placeholder="e.g. Launching a plant-based protein bar in the UK — need 3 fitness creators to show it fits into their post-workout routine"
                      className="bg-white/[0.02] border-white/10 text-white"
                      data-testid="ai-assist-input"
                    />
                    <div className="flex justify-end">
                      <Button type="button" onClick={runAiAssist} disabled={aiBusy} className="nexus-gradient rounded-full text-white border-0 font-semibold hover:opacity-90" data-testid="ai-assist-run">
                        {aiBusy ? "Drafting…" : (<><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Draft brief</>)}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Title</Label>
                <Input data-testid="campaign-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Summer sunscreen launch — 3 UGC creators" className="mt-1.5 bg-white/[0.02] border-white/10 text-white h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Description</Label>
                <Textarea data-testid="campaign-description-input" value={form.description} onChange={(e) => set("description", e.target.value)} rows={5} placeholder="Who is your brand? What are you launching? Tone, must-haves, don'ts…" className="mt-1.5 bg-white/[0.02] border-white/10 text-white" />
                <p className="text-xs text-zinc-500 mt-1.5">Tip: the more specific your brief, the higher-quality your pitches will be.</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Deliverables</Label>
                <Textarea data-testid="campaign-deliverables-input" value={form.deliverables} onChange={(e) => set("deliverables", e.target.value)} rows={3} placeholder="e.g. 3× Reels + 3× Stories, one revision round, brand tagged" className="mt-1.5 bg-white/[0.02] border-white/10 text-white" />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6" data-testid="step-pricing">
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2"><WalletIcon className="h-3.5 w-3.5" /> Pricing mode</div>
                <div className="grid md:grid-cols-2 gap-3">
                  {[
                    { key: "fixed", label: "Fixed fee", desc: "You set the price. Creators accept and start.", icon: Handshake },
                    { key: "bidding", label: "Open to bids", desc: "Creators propose their own rate. You pick the best.", icon: Target },
                  ].map((opt) => {
                    const Icon = opt.icon;
                    const active = form.pricing_mode === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => set("pricing_mode", opt.key)}
                        data-testid={`pricing-mode-${opt.key}`}
                        className={`p-4 rounded-xl text-left border transition-colors ${active ? "border-nexus-purple bg-nexus-purple/10 nexus-border-glow" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className={`h-4 w-4 ${active ? "text-nexus-pink" : "text-zinc-400"}`} />
                          <div className="font-semibold text-sm">{opt.label}</div>
                        </div>
                        <div className="text-xs text-zinc-400">{opt.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">{form.pricing_mode === "bidding" ? "Budget ceiling (£)" : "Fixed fee (£)"}</Label>
                  <Input type="number" min={0} data-testid="campaign-budget-input" value={form.budget} onChange={(e) => set("budget", e.target.value)} className="mt-1.5 bg-white/[0.02] border-white/10 text-white h-11" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Application window (days)</Label>
                  <Input type="number" min={1} max={180} data-testid="campaign-deadline-input" value={form.deadline_days} onChange={(e) => set("deadline_days", e.target.value)} className="mt-1.5 bg-white/[0.02] border-white/10 text-white h-11" />
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2"><Target className="h-3.5 w-3.5" /> Campaign targets <span className="text-zinc-500 normal-case tracking-normal text-[10px]">— optional motivation tracker</span></div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {[
                    { k: "ads_per_week", label: "Ads/week" },
                    { k: "views", label: "Views" },
                    { k: "engagement", label: "Engagement" },
                    { k: "conversions", label: "Conversions" },
                    { k: "sales", label: "Sales" },
                  ].map((t) => (
                    <div key={t.k}>
                      <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{t.label}</Label>
                      <Input type="number" min={0} value={form.targets[t.k]} onChange={(e) => setTarget(t.k, e.target.value)} className="mt-1 bg-white/[0.02] border-white/10 text-white text-sm h-9" data-testid={`target-${t.k}`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6" data-testid="step-requirements">
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Niches</div>
                <div className="flex flex-wrap gap-2">
                  {NICHES.map((n) => (
                    <button key={n} type="button" onClick={() => set("niches", toggle(form.niches, n))} data-testid={`campaign-niche-${n}`} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.niches.includes(n) ? "border-nexus-purple bg-nexus-purple/15 text-white" : "border-white/10 text-zinc-300 hover:border-white/25"}`}>{n}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Collab on</div>
                <div className="flex flex-wrap gap-2">
                  {PLATFORMS.map((p) => {
                    const on = form.platforms.includes(p.key);
                    const isAd = p.key === "ad_advertising";
                    return (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => set("platforms", toggle(form.platforms, p.key))}
                        data-testid={`campaign-platform-${p.key}`}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on
                          ? (isAd ? "border-nexus-pink bg-nexus-pink/15 text-white" : "border-nexus-blue bg-nexus-blue/10 text-white")
                          : "border-white/10 text-zinc-300 hover:border-white/25"}`}
                      >
                        {isAd && <span className="mr-1">🚀</span>}
                        {p.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-zinc-500 mt-2">Pick every channel this campaign should go live on. Add <span className="text-nexus-pink">Ad advertising</span> if you plan to boost the content as paid social ads (Meta Partnership Ads, TikTok Spark, YouTube Ads).</p>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2"><Film className="h-3.5 w-3.5" /> Content requirements</div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => set("edited_content_required", !form.edited_content_required)} data-testid="toggle-edited" className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.edited_content_required ? "border-nexus-blue bg-nexus-blue/10 text-white" : "border-white/10 text-zinc-300 hover:border-white/25"}`}>
                    Edited content
                  </button>
                  <button type="button" onClick={() => set("raw_footage_required", !form.raw_footage_required)} data-testid="toggle-raw-footage" className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.raw_footage_required ? "border-nexus-pink bg-nexus-pink/10 text-white" : "border-white/10 text-zinc-300 hover:border-white/25"}`}>
                    Include raw footage
                  </button>
                </div>
              </div>
              <div className="rounded-2xl bg-white/[0.02] hairline p-4">
                <DeliverableSpecEditor spec={form.spec} onChange={(v) => set("spec", v)} />
              </div>
              <div className="rounded-2xl bg-white/[0.02] hairline p-4">
                <CampaignQuestionsEditor questions={form.questions} onChange={(v) => set("questions", v)} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2"><Paperclip className="h-3.5 w-3.5" /> Brief files</div>
                <div className="space-y-2 mb-2">
                  {form.brief_files.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.02] hairline text-xs">
                      <Paperclip className="h-3.5 w-3.5 text-zinc-400" />
                      <a href={f.url} target="_blank" rel="noreferrer" className="flex-1 text-zinc-200 truncate hover:text-white">{f.name}</a>
                      <button type="button" onClick={() => removeBriefFile(i)} className="text-zinc-500 hover:text-nexus-pink"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/5 border border-white/15 text-sm text-zinc-200 hover:bg-white/10 cursor-pointer transition-colors" data-testid="brief-file-upload">
                  <Paperclip className="h-4 w-4" /> Upload brief file
                  <input type="file" onChange={uploadBrief} className="hidden" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" />
                </label>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2"><LinkIcon className="h-3.5 w-3.5" /> Inspiration links</div>
                <div className="space-y-2">
                  {form.inspiration_links.map((l, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input value={l} onChange={(e) => setLink(i, e.target.value)} placeholder="https://tiktok.com/@… or Google Drive link" className="bg-white/[0.02] border-white/10 text-white" data-testid={`inspiration-link-${i}`} />
                      <button type="button" onClick={() => removeLink(i)} className="text-zinc-500 hover:text-nexus-pink p-2"><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                  <button type="button" onClick={addLink} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-nexus-blue hover:text-white" data-testid="add-inspiration-link"><Plus className="h-3.5 w-3.5" /> Add link</button>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2"><Truck className="h-3.5 w-3.5" /> Shipping</div>
                <button type="button" onClick={() => set("needs_shipping", !form.needs_shipping)} data-testid="toggle-shipping" className={`px-3 py-1.5 rounded-full text-sm border transition-colors mb-3 ${form.needs_shipping ? "border-nexus-blue bg-nexus-blue/10 text-white" : "border-white/10 text-zinc-300 hover:border-white/25"}`}>
                  {form.needs_shipping ? "✓ Product ships to creator" : "Add shipping requirement"}
                </button>
                {form.needs_shipping && (
                  <Textarea value={form.shipping_note} onChange={(e) => set("shipping_note", e.target.value)} rows={2} placeholder="What you'll ship, tracking, expected arrival window…" className="bg-white/[0.02] border-white/10 text-white" data-testid="shipping-note-input" />
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="step-review">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500">
                <Sparkles className="h-4 w-4 text-nexus-pink" /> Review & publish
              </div>
              <CampaignPredictor form={form} />
              <div className="rounded-2xl bg-white/[0.02] hairline p-5 space-y-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Title</div>
                  <div className="font-display font-bold text-xl mt-0.5" data-testid="review-title">{form.title || <span className="text-zinc-500">— empty —</span>}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Description</div>
                  <p className="text-sm text-zinc-300 mt-1 whitespace-pre-wrap">{form.description || <span className="text-zinc-500">— empty —</span>}</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-white/5">
                  <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Mode</div><div className="text-sm text-zinc-200 mt-0.5">{form.pricing_mode === "bidding" ? "Open to bids" : "Fixed fee"}</div></div>
                  <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">{form.pricing_mode === "bidding" ? "Ceiling" : "Fee"}</div><div className="text-sm text-zinc-200 mt-0.5" data-testid="review-budget">£{Number(form.budget).toLocaleString()}</div></div>
                  <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Window</div><div className="text-sm text-zinc-200 mt-0.5">{form.deadline_days} days</div></div>
                  <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Platforms</div><div className="text-sm text-zinc-200 mt-0.5 truncate">{form.platforms.join(", ") || "—"}</div></div>
                </div>
                <div className="pt-2 border-t border-white/5">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Deliverable spec</div>
                  <DeliverableSpecTable spec={form.spec} />
                </div>
                {form.questions.length > 0 && (
                  <div className="pt-2 border-t border-white/5" data-testid="review-questions">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Screening questions ({form.questions.length})</div>
                    <ol className="text-sm text-zinc-300 space-y-1 list-decimal pl-4">{form.questions.map((q) => <li key={q.id}>{q.label}</li>)}</ol>
                  </div>
                )}
                {form.niches.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Niches</div>
                    <div className="flex flex-wrap gap-1.5">{form.niches.map((n) => <span key={n} className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border border-white/10 text-zinc-300">{n}</span>)}</div>
                  </div>
                )}
              </div>
              <p className="text-xs text-zinc-500">Once you post, creators can apply immediately. You can edit or close the campaign anytime.</p>
            </div>
          )}

          {/* Nav */}
          <div className="flex items-center justify-between pt-4 border-t border-white/5">
            <Button type="button" onClick={back} disabled={step === 0} variant="outline" className="bg-white/5 border-white/15 rounded-full text-white hover:bg-white/10 hover:text-white disabled:opacity-30" data-testid="wizard-back-btn">
              <ChevronLeft className="h-4 w-4 mr-1.5" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button type="button" onClick={next} className="nexus-gradient rounded-full text-white border-0 font-semibold hover:opacity-90" data-testid="wizard-next-btn">
                Next <ChevronRight className="h-4 w-4 ml-1.5" />
              </Button>
            ) : (
              <Button type="button" onClick={submit} disabled={busy} className="nexus-gradient rounded-full text-white border-0 font-semibold hover:opacity-90" data-testid="campaign-submit-btn">
                {busy ? "Posting…" : "Post campaign"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </BrandShell>
  );
}

/* ---------- Campaign Predictor widget ---------- */
function CampaignPredictor({ form }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);

  const run = async () => {
    setLoading(true); setErr(false);
    try {
      const r = await api.post("/brand/campaign-predictor", {
        title: form.title,
        description: form.description,
        budget: Number(form.budget) || 0,
        niches: form.niches,
        platforms: form.platforms,
        pricing_mode: form.pricing_mode,
      });
      setData(r.data);
    } catch { setErr(true); }
    finally { setLoading(false); }
  };
  useEffect(() => { run(); }, []);

  if (loading) return (
    <div className="rounded-2xl border border-nexus-purple/30 bg-nexus-purple/[0.06] p-5 text-sm text-zinc-400 animate-pulse" data-testid="predictor-loading">
      <Sparkles className="h-4 w-4 text-nexus-pink inline mr-2" /> Analyzing your brief…
    </div>
  );
  if (err || !data) return null;

  const scoreColor = data.quality_score >= 70 ? "text-emerald-300" : data.quality_score >= 50 ? "text-nexus-blue" : "text-yellow-300";

  return (
    <div className="rounded-2xl border border-nexus-purple/30 bg-gradient-to-br from-nexus-purple/[0.08] to-nexus-blue/[0.04] p-5" data-testid="predictor-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-300">
          <Sparkles className="h-4 w-4 text-nexus-pink" /> Campaign predictor
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-nexus-pink/15 text-nexus-pink">AI</span>
        </div>
        <div className={`font-display font-bold text-2xl tabular-nums ${scoreColor}`} data-testid="predictor-score">{data.quality_score}<span className="text-xs text-zinc-500">/100</span></div>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Matching creators</div>
          <div className="font-display font-bold text-xl tabular-nums" data-testid="predictor-matching">{data.matching_creators}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Est. applications</div>
          <div className="font-display font-bold text-xl tabular-nums" data-testid="predictor-applications">{data.estimated_applications}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Typical bids</div>
          <div className="font-display font-bold text-xl tabular-nums" data-testid="predictor-bids">£{data.typical_bid_low}–£{data.typical_bid_high}</div>
        </div>
      </div>
      <div className="pt-3 border-t border-white/5">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Verdict · {data.verdict}</div>
        <p className="text-sm text-zinc-200" data-testid="predictor-tip">{data.tip}</p>
      </div>
    </div>
  );
}
